import { FeatureGate } from '../components/FeatureGate';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchProject } from '../api';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { PageHeader } from '../components/PageHeader';
import { AgentsPanel } from '../components/AgentsPanel';
import { IntegrationTokensCard } from '../components/IntegrationTokensCard';
import { ProjectFeedbackCard } from '../components/ProjectFeedbackCard';
import { useCapabilities } from '../hooks/useCapabilities';
import { ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * The project's (or prototype's) Team page: the fixed roster — Product Manager
 * and Engineer, always present — plus the one Autonomy dial that decides how
 * much they do without you. The roster + dial live in AgentsPanel. Owners
 * also manage the project's Integration API tokens and (repo projects) native
 * end-user feedback here.
 */
export function AgentsPage() {
  const { projectId = '' } = useParams();
  const { wp } = useWorkspacePath();
  const { can } = useCapabilities();

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });

  // Keep the URL on the pretty code; drive the panel off the resolved UUID.
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  const resolvedId = projectQuery.data?.id ?? projectId;

  useDocumentTitle(
    projectQuery.data ? `Team · ${projectQuery.data.name}` : 'Team',
  );

  if (projectQuery.isLoading) return <LoadingState label="Loading team…" />;
  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data!;
  const nav = projectNav(project);

  return (
    <div className="space-y-6 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          { label: 'Team' },
        ]}
      />

      <PageHeader
        title="Team"
        subtitle="Your project's team: a Product Manager who plans incoming tickets and an Engineer who builds and ships them. Pause the team from here."
      />

      <ProjectSectionNav
        kind={project.kind}
        projectRef={project.code ?? project.id}
        active="team"
      />

      <AgentsPanel projectId={resolvedId} />

      {/* Prototypes keep their per-deployment card on the prototype settings page. */}
      {project.kind !== 'prototype' && can('project:manage') && (
        <ProjectFeedbackCard projectId={project.id} />
      )}

      {can('integrations:manage', { projectId: project.id }) && (
        <FeatureGate feature="integrationTokens">
          <IntegrationTokensCard projectId={project.id} />
        </FeatureGate>
      )}
    </div>
  );
}
